import { getUriAndCacheResponse } from "@/_services/cache"
import { loc } from "@/_services/localization"
import { getUsername } from "@/_services/userConfiguration"
import { getEntityName, getEntityServerRoute } from "basikon-common-utils"
import React from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { withRouter } from "react-router-dom"

class DebugPopover extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      showSelectDebug: false,
      selectName: null,
      fieldDescription: undefined,
    }
  }

  componentDidMount = () => {
    const { select, debugScriptList } = this.props
    const selectName = typeof select === "string" ? select : debugScriptList
    this.setState({ showSelectDebug: select && selectName, selectName })
    this.resolveFieldDescription()
  }

  componentDidUpdate = prevProps => {
    const { debug, modelFieldPath } = this.props
    const { debug: prevDebug, modelFieldPath: prevModelFieldPath } = prevProps

    if (prevDebug !== debug || prevModelFieldPath !== modelFieldPath) this.resolveFieldDescription()
  }

  // Resolve the field's schema description (same text as the OpenAPI description)
  resolveFieldDescription = async () => {
    const { debug, help, modelFieldPath } = this.props
    if (!debug || help || !modelFieldPath || this.state.fieldDescription !== undefined) return

    try {
      const pathname = this.props.location?.pathname || window?.location?.pathname
      const entityName = getEntityNameFromRoute(pathname)
      // Only Product and Scheme schemas are documented for now.
      if (!["Product", "Scheme"].includes(entityName)) return
      const serverRoute = getEntityServerRoute(entityName)
      if (!serverRoute) return
      const schemaInfo = await getUriAndCacheResponse(`${serverRoute}-schema`)
      const fieldDescription = resolveSchemaFieldDescription(schemaInfo?.schema, modelFieldPath)
      if (fieldDescription) this.setState({ fieldDescription })
    } catch {
      // ignore: the tooltip simply keeps showing the field path only
    }
  }

  handleConfigLink = event => {
    const { selectName, showSelectDebug } = this.state
    const { debugUrlQuery, debugScriptList, onClick, onMouseDown, history } = this.props

    if (typeof onClick === "function") return onClick(event)
    if (typeof onMouseDown === "function") return onMouseDown(event)
    if (!showSelectDebug) return

    const username = getUsername()
    const debugUrl = `/scripts?${
      debugUrlQuery || `selectedSettingType=${debugScriptList ? "script" : "list"}&name=${selectName}&filter=${selectName}&username=${username}`
    }`
    if (event?.button === 1) {
      window.open(`${window.location.origin}${debugUrl}&all=1`, "_blank")
    } else {
      history.push(debugUrl)
    }
  }

  render() {
    const { selectName, showSelectDebug, fieldDescription } = this.state
    const { debug, help, onClick, modelFieldPath, debugScriptList, select, fieldDebugBtn, value, spanClassName = "" } = this.props
    const isProvidedSelectList = typeof select !== "string" && !!select

    if (!debug) return null

    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id="field">
            {help || (
              <>
                <span>{modelFieldPath}</span>
                {fieldDescription && <div className="font-style-italic mt-5px">{fieldDescription}</div>}
                {showSelectDebug && !debugScriptList && (
                  <span>
                    {!value && ` (${loc`List`}: ${selectName})`}
                    {value && ` (${loc`List`}: ${selectName}; ${loc`Value`}: ${value})`}
                  </span>
                )}
                {showSelectDebug && debugScriptList && <span> {`(${loc`Script`} ${selectName})`}</span>}
                {!showSelectDebug && isProvidedSelectList && (
                  <span className="font-style-italic">
                    {!value && ` (${loc`Custom`})`}
                    {value && ` (${loc`Custom`}; ${loc`Value`}: ${value})`}
                  </span>
                )}
              </>
            )}
          </Tooltip>
        }
      >
        <span
          className={`${spanClassName} field-debug${showSelectDebug ? " field-debug-link" : ""}${fieldDebugBtn ? " field-debug-btn" : ""}${
            help && !onClick ? " field-debug-help" : ""
          }`}
          onClick={() => this.handleConfigLink()}
          onMouseDown={e => this.handleConfigLink(e)}
        >
          ?
        </span>
      </OverlayTrigger>
    )
  }
}

export default withRouter(DebugPopover)

function resolveSchemaFieldDescription(schema, modelFieldPath) {
  if (!schema || !modelFieldPath) return undefined

  // Normalize the various array notations the app uses ("a[0].b", "a[].b", "a.0.b") into plain steps ["a", "b"]
  const segments = modelFieldPath
    .replace(/\[\d*\]/g, ".")
    .split(".")
    .filter(Boolean)

  let node = schema
  for (const segment of segments) {
    if (Array.isArray(node)) node = node[0]
    if (!node || typeof node !== "object") return undefined
    if (/^\d+$/.test(segment)) continue // dot-style array index (e.g. "a.0.b")
    node = node[segment]
  }
  if (Array.isArray(node)) node = node[0]

  return node && typeof node === "object" ? node.description : undefined
}

// Derive the entity name from a client route: "/product/PRD00001" or "/product" both map to "Product"
function getEntityNameFromRoute(pathname) {
  if (!pathname || pathname.endsWith("/")) return undefined
  const parts = pathname.split("/")
  const segmentCount = parts.length - 1
  if (segmentCount !== 1 && segmentCount !== 2) return undefined
  return getEntityName({ clientRoute: `/${parts[1]}` })
}
